import { useState, useEffect, useCallback } from "react";
import { api } from "~/api";
import { Loader } from "@googlemaps/js-api-loader";
import { locationList, locationInfoAtom, getCurrentCustomerId } from "~/AtomWithDynamoDB";
import { useAtom } from "jotai/index";
import { categoryAtom } from "~/Atom";
import { useTranslation } from "react-i18next";

let loader: Loader | null = null;

/**
 * Google Maps APIのローダーを取得する
 *
 * @param apiKey APIキー
 * @returns Loader
 */
function getLoader(apiKey: string) {
  if (!loader) {
    loader = new Loader({
      apiKey: apiKey,
      version: "weekly",
      libraries: ["places", "geometry"],
    });
  }
  return loader;
}

/**
 * Google Maps APIキーを取得する
 *
 * @returns APIキー
 */
const fetchKey = async () => {
  const customerId = await getCurrentCustomerId();
  const key = await api.googlemap.getKey.query({ customerId });
  return key;
};

const DEFAULT_CENTER = {
  lat: locationList.lat,
  lng: locationList.lon,
};

// 施設の状態
export interface PlaceState {
  name: string;
  distance: number;
  position: google.maps.LatLngLiteral;
  placeId: string;
}

// 施設の詳細情報
export interface PlaceDetail {
  name?: string;
  address?: string;
  url?: string;
  website?: string;
  opening_hours?: string[];
  phone?: string;
}

// 返り値の型
export interface UseGoogleMapReturn {
  apiKey: string;
  address: string;
  setAddress: (v: string) => void;
  results: PlaceState[];
  center: google.maps.LatLngLiteral;
  selectedIdx: number | null;
  clickedPosition: google.maps.LatLngLiteral | null;
  selectedPlaceDetail: PlaceDetail | null;
  directions: google.maps.DirectionsResult | null;
  placeDurations: { [mode: string]: string } | null;
  handleSearch: () => Promise<void>;
  fetchPlaceDetail: (idx: number) => Promise<void>;
  fetchPlaceDetailByPlaceId: (placeId: string, latLng?: google.maps.LatLng | null) => void;
  handleMapClick: (e: google.maps.MapMouseEvent) => void;
  handleCloseInfoWindow: () => void;
  t: (k: string) => string;
}

/**
 * Google Maps APIを使用するためのカスタムフック
 *
 * @param travelMode "WALKING" | "DRIVING" | "BICYCLING" | "TRANSIT"
 * @returns
 */
export function useGoogleMap(
  travelMode?: "WALKING" | "DRIVING" | "BICYCLING" | "TRANSIT"
): UseGoogleMapReturn {
  const { t } = useTranslation();
  const [address, setAddress] = useState("");
  const [apiKey, setApiKey] = useState("");
  const [results, setResults] = useState<PlaceState[]>([]);
  const [center, setCenter] = useState<google.maps.LatLngLiteral>(DEFAULT_CENTER);
  const [selectedIdx, setSelectedIdx] = useState<number | null>(null);
  const [category] = useAtom(categoryAtom);
  const [clickedPosition, setClickedPosition] = useState<google.maps.LatLngLiteral | null>(null);
  const [selectedPlaceDetail, setSelectedPlaceDetail] = useState<PlaceDetail | null>(null);
  const [directions, setDirections] = useState<google.maps.DirectionsResult | null>(null);
  const [placeDurations, setPlaceDurations] = useState<{ [mode: string]: string } | null>(null);
  const [googleApiObj, setGoogleApiObj] = useState<typeof google | null>(null);
  const [locationInfo] = useAtom(locationInfoAtom);

  // locationInfo更新時にcenterも更新
  useEffect(() => {
    if (locationInfo.lat && locationInfo.lon) {
      setCenter({ lat: locationInfo.lat, lng: locationInfo.lon });
    }
  }, [locationInfo.lat, locationInfo.lon]);

  /**
   * リンクを開かないようにする
   *
   */
  useEffect(() => {
    const observer = new MutationObserver(() => {
      const links = document.querySelectorAll<HTMLAnchorElement>("a");
      links.forEach((link) => {
        const href = link.getAttribute("href");
        if (href) {
          link.addEventListener("click", (e) => {
            e.preventDefault();
          });
          link.setAttribute("target", "_self"); // 新しいタブ防止
        }
      });
    });
    observer.observe(document.body, { childList: true, subtree: true });

    return () => observer.disconnect();
  }, []);

  // 経路再計算（travelMode変更時）
  useEffect(() => {
    if (selectedIdx !== null && results[selectedIdx]) {
      fetchDirections(results[selectedIdx].position);
      fetchAllDurations(results[selectedIdx].position);
    } else {
      setDirections(null);
      setPlaceDurations(null);
    }
  }, [travelMode]);

  // APIキー取得 & Loader初期化
  useEffect(() => {
    const key = fetchKey();
    key.then(setApiKey).catch((err) => console.error("Failed to fetch Google Maps API Key:", err));
  }, []);

  // Google Maps APIの初期化
  useEffect(() => {
    if (!apiKey) return;
    const initLoader = async () => {
      const newLoader = getLoader(apiKey);
      const google = await newLoader.load();
      setGoogleApiObj(google);
    };
    initLoader();
  }, [apiKey]);

  // 施設検索
  const handleSearch = async () => {
    if (!apiKey || !locationInfo.address) return;
    try {
      const geoRes = await fetch(
        `https://maps.googleapis.com/maps/api/geocode/json?address=${encodeURIComponent(locationInfo.address)}&key=${apiKey}`
      );
      const geoData = await geoRes.json();
      if (geoData.status !== "OK" || !geoData.results || geoData.results.length === 0) {
        alert(t("notFound"));
        return;
      }
      const location = geoData.results[0].geometry.location;
      setCenter(location);

      const mapDiv = document.createElement("div");
      const map = new google.maps.Map(mapDiv);
      const service = new google.maps.places.PlacesService(map);
      const request: google.maps.places.PlaceSearchRequest = {
        location,
        radius: 2000,
        type: category as string,
      };
      service.nearbySearch(
        request,
        (
          places: google.maps.places.PlaceResult[] | null,
          status: google.maps.places.PlacesServiceStatus
        ) => {
          if (status === google.maps.places.PlacesServiceStatus.OK && places) {
            const resultData: PlaceState[] = places.map((place) => {
              const loc = place.geometry?.location;
              const latLng = loc
                ? { lat: loc.lat(), lng: loc.lng() }
                : { lat: location.lat, lng: location.lng };
              const origin = new google.maps.LatLng(location);
              const distance = loc
                ? google.maps.geometry.spherical.computeDistanceBetween(origin, loc)
                : 0;
              return {
                name: place.name || "unknown",
                distance: Math.round(distance),
                position: latLng,
                placeId: place.place_id || "",
              };
            });
            setResults(resultData.sort((a, b) => a.distance - b.distance));
          } else {
            alert(t("fetchNearbyFailed") + status);
            console.error("Places API Error:", status);
          }
        }
      );
    } catch (err) {
      alert(t("errorOccurred") + err);
    }
  };

  // 全移動手段の所要時間を取得
  const fetchAllDurations = async (destPos: google.maps.LatLngLiteral) => {
    if (!googleApiObj) return;
    const directionsService = new googleApiObj.maps.DirectionsService();
    const modes = ["WALKING", "DRIVING", "BICYCLING", "TRANSIT"];
    const durationMap: { [mode: string]: string } = {};
    await Promise.all(
      modes.map(
        (mode) =>
          new Promise<void>((resolve) => {
            directionsService.route(
              { origin: center, destination: destPos, travelMode: mode as google.maps.TravelMode },
              (
                result: google.maps.DirectionsResult | null,
                status: google.maps.DirectionsStatus
              ) => {
                if (status === googleApiObj!.maps.DirectionsStatus.OK && result?.routes?.[0]) {
                  const duration = result.routes[0].legs[0].duration?.text;
                  if (duration) durationMap[mode] = duration;
                }
                resolve();
              }
            );
          })
      )
    );
    setPlaceDurations(durationMap);
  };

  // 経路取得
  const fetchDirections = useCallback(
    (destPos: google.maps.LatLngLiteral) => {
      if (!googleApiObj) return;
      setDirections(null);
      const directionsService = new googleApiObj.maps.DirectionsService();
      const requestedMode = (travelMode ?? "WALKING") as google.maps.TravelMode;
      directionsService.route(
        { origin: center, destination: destPos, travelMode: requestedMode },
        (result: google.maps.DirectionsResult | null, status: google.maps.DirectionsStatus) => {
          if (status === googleApiObj.maps.DirectionsStatus.OK && result) {
            setDirections(result);
          } else {
            setDirections(null);
          }
        }
      );
    },
    [center, travelMode, googleApiObj]
  );

  // 施設詳細情報取得
  const fetchPlaceDetail = async (idx: number) => {
    const selectedPlace = results[idx];
    if (!selectedPlace || !selectedPlace.placeId) return;
    setSelectedIdx(idx);
    setClickedPosition(selectedPlace.position);
    if (!googleApiObj) return;
    fetchDirections(selectedPlace.position);
    await fetchAllDurations(selectedPlace.position);

    const mapDiv = document.createElement("div");
    const map = new googleApiObj.maps.Map(mapDiv);
    const service = new googleApiObj.maps.places.PlacesService(map);
    service.getDetails(
      {
        placeId: selectedPlace.placeId,
        fields: [
          "name",
          "formatted_address",
          "url",
          "website",
          "opening_hours",
          "formatted_phone_number",
        ],
      },
      (
        placeResult: google.maps.places.PlaceResult | null,
        status: google.maps.places.PlacesServiceStatus
      ) => {
        if (status === googleApiObj.maps.places.PlacesServiceStatus.OK && placeResult) {
          setSelectedPlaceDetail({
            name: placeResult.name,
            address: placeResult.formatted_address,
            url: placeResult.url,
            website: placeResult.website,
            opening_hours: placeResult.opening_hours?.weekday_text ?? [],
            phone: placeResult.formatted_phone_number,
          });
        } else {
          setSelectedPlaceDetail({ name: selectedPlace.name });
        }
      }
    );
  };

  // placeIdから施設詳細情報取得
  const fetchPlaceDetailByPlaceId = (placeId: string, latLng?: google.maps.LatLng | null) => {
    if (!googleApiObj) return;
    setSelectedIdx(null);
    setDirections(null);
    let pos: google.maps.LatLngLiteral | undefined;
    if (latLng) pos = { lat: latLng.lat(), lng: latLng.lng() };
    const mapDiv = document.createElement("div");
    const map = new googleApiObj.maps.Map(mapDiv);
    const service = new googleApiObj.maps.places.PlacesService(map);
    service.getDetails(
      {
        placeId,
        fields: [
          "name",
          "formatted_address",
          "url",
          "website",
          "opening_hours",
          "formatted_phone_number",
          "geometry",
        ],
      },
      (
        placeResult: google.maps.places.PlaceResult | null,
        status: google.maps.places.PlacesServiceStatus
      ) => {
        if (status === googleApiObj.maps.places.PlacesServiceStatus.OK && placeResult) {
          setSelectedPlaceDetail({
            name: placeResult.name,
            address: placeResult.formatted_address,
            url: placeResult.url,
            website: placeResult.website,
            opening_hours: placeResult.opening_hours?.weekday_text ?? [],
            phone: placeResult.formatted_phone_number,
          });
          let destPos: google.maps.LatLngLiteral | undefined;
          if (placeResult.geometry?.location) {
            destPos = {
              lat: placeResult.geometry.location.lat(),
              lng: placeResult.geometry.location.lng(),
            };
            setClickedPosition(destPos);
          } else if (pos) {
            destPos = pos;
            setClickedPosition(pos);
          }
          if (destPos) {
            fetchDirections(destPos);
            fetchAllDurations(destPos);
          } else {
            setDirections(null);
            setPlaceDurations(null);
          }
        } else {
          setSelectedPlaceDetail({ name: "(詳細情報取得失敗)" });
          setClickedPosition(pos || null);
          setDirections(null);
          setPlaceDurations(null);
        }
      }
    );
  };

  // 地図クリック時の処理
  const handleMapClick = (e: google.maps.MapMouseEvent) => {
    const maybeWithPlaceId = e as google.maps.MapMouseEvent & { placeId?: string };
    if (maybeWithPlaceId.placeId) {
      e.stop();
      fetchPlaceDetailByPlaceId(maybeWithPlaceId.placeId, e.latLng);
    }
  };

  // 選択クリア（InfoWindow を閉じた時などに呼ぶ）
  const handleCloseInfoWindow = useCallback(() => {
    setClickedPosition(null);
    setSelectedPlaceDetail(null);
  }, []);

  // 初期化 & 検索
  useEffect(() => {
    setResults([]);
    setSelectedIdx(null);
    setClickedPosition(null);
    setSelectedPlaceDetail(null);
    setDirections(null);
    setPlaceDurations(null);
    handleSearch();
  }, [category, apiKey, locationInfo.address]);
  return {
    apiKey,
    address,
    setAddress: (v: string) => setAddress(v),
    results,
    center,
    selectedIdx,
    clickedPosition,
    selectedPlaceDetail,
    directions,
    placeDurations,
    handleSearch,
    fetchPlaceDetail,
    fetchPlaceDetailByPlaceId,
    handleMapClick,
    handleCloseInfoWindow,
    t,
  };
}
